import React, { useCallback, useImperativeHandle, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import CardTemplate from "components/card/card-template/CardTemplate";
import { useQuote } from "hooks/useQuote";
import { useContactInformation } from "hooks/useContactInformation";
import type { DTOApplicationFormReturn, CoverageReplacementRef } from "types/Card";
import { CoverageReplacementStateSchema } from "components/card/coverage-replacement/CoverageReplacementStateSchema";
import { alphaNumericRegex, alphaWithSpacesRegex } from "constants/validations";
import {
    restoreFormData,
    handleReplacementChange,
    extractReplacementValues,
    GIWL_COVERAGE_REPLACEMENT_READONLY_STATES,
} from "components/card/coverage-replacement/coverageReplacementHelpers";
import { ROUTES } from "routes/constants";
import { useProductContext } from "context/ProductContext";
import InfoCoverageReplacementModal from "components/modal/info-coverage-replacement/InfoCoverageReplacementModal";

interface CoverageReplacementProps {
    className?: string;
    form: DTOApplicationFormReturn;
}

type CoverageReplacementWithRef = CoverageReplacementProps & {
    ref?: React.Ref<CoverageReplacementRef>;
};

/**
 * Florida state restriction notice for DTO replacement policies
 */
const FloridaReplacementNotice = () => (
    <div className="bg-error/10 rounded-2xl p-4 mt-2">
        <p className="text-error text-sm leading-5 font-medium">
            We're sorry, but AAA Life is not able to accept applications online for replacement policies in your state. To
            complete your application by phone, please call
            <span className="font-bold text-info-content"> (888) 616-8977 </span>
            (8 a.m. – 10 p.m. ET Mon.-Fri., 11 a.m. – 3 p.m. Sat.) or return the paper application you received in the mail.
        </p>
    </div>
);

/**
 * Replacement policy details form fields
 */
interface ReplacementDetailsProps {
    register: DTOApplicationFormReturn["register"];
    setValue: DTOApplicationFormReturn["setValue"];
    errors: DTOApplicationFormReturn["formState"]["errors"];
}

const ReplacementDetails = ({ register, setValue, errors }: ReplacementDetailsProps) => (
    <div className="flex flex-col mt-2 gap-2">
        <div className="form-control w-full">
            <label className="label" htmlFor="replacementCompany">
                <span className="text-base-content">
                    Which insurance company is your current policy with?
                    <span className="text-error ml-1">*</span>
                </span>
            </label>
            <input
                {...register("coverageReplacement.replacementCompany", {
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value.replace(alphaWithSpacesRegex, "");
                        setValue("coverageReplacement.replacementCompany", value);
                    },
                })}
                type="text"
                id="replacementCompany"
                placeholder="Life Insurance Company"
                className={`input input-bordered w-full bg-base-100 placeholder-text ${
                    errors.coverageReplacement?.replacementCompany ? "input-error" : ""
                }`}
                maxLength={70}
            />
            {errors.coverageReplacement?.replacementCompany && (
                <p className="text-error text-xs mt-1">{errors.coverageReplacement.replacementCompany.message}</p>
            )}
        </div>

        <div className="form-control w-full">
            <label className="label" htmlFor="replacementAccount">
                <span className="text-base-content">Policy Number</span>
            </label>
            <input
                {...register("coverageReplacement.replacementAccount", {
                    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value.replace(alphaNumericRegex, "");
                        setValue("coverageReplacement.replacementAccount", value);
                    },
                })}
                type="text"
                id="replacementAccount"
                placeholder="1234567890"
                className="input input-bordered w-full bg-base-100 placeholder-text"
                maxLength={25}
            />
        </div>
    </div>
);

const CoverageReplacement = ({ ref, ...props }: CoverageReplacementWithRef) => {
    const { className = "", form } = props;
    // ========== Hooks ==========
    const navigate = useNavigate();
    const { quoteData } = useQuote();
    const { isGIWL } = useProductContext();
    const { contactData } = useContactInformation();
    const {
        register,
        formState: { errors },
        watch,
        setValue,
        trigger,
    } = form;

    // ========== State ==========
    const coverageReplacementDataRedux = contactData?.coverageReplacement;
    const isReplacing = watch("coverageReplacement.isReplacing");

    // ========== Computed Values ==========
    const state = useMemo(
        () => ({
            application: quoteData
                ? {
                      state: quoteData.state,
                  }
                : null,
        }),
        [quoteData]
    );

    const coverageQuestion = isGIWL
        ? "Will this policy be replacing an existing policy?"
        : "Is the insurance applied for intended to replace, discontinue, or change any existing insurance or annuity?";

    const parseResult = useMemo(() => CoverageReplacementStateSchema.safeParse(state), [state]);
    const validatedState = parseResult.success ? parseResult.data : null;
    const applicationData = validatedState?.application;
    const userState = applicationData?.state;

    // ========== Computed Values ==========
    // GIWL states where replacement is unavailable: the answer is locked to "No".
    const isGiwlReplacementReadOnly = isGIWL && GIWL_COVERAGE_REPLACEMENT_READONLY_STATES.includes(userState ?? "");
    const isReplacementYes = !isGiwlReplacementReadOnly && isReplacing === true;
    const isReplacementNo = isGiwlReplacementReadOnly || isReplacing === false;
    const isFloridaReplacement = !isGIWL && userState === "FL" && isReplacing === true;
    const shouldShowReplacementFields = isReplacementYes && (isGIWL || userState !== "FL");

    // ========== Effects ==========
    // Validate state
    useEffect(() => {
        if (!parseResult.success) {
            console.error("CoverageReplacement state validation error:", parseResult.error);
            navigate(ROUTES.SOMETHING_WENT_WRONG);
        }
    }, [parseResult.success, parseResult.error, navigate]);

    // Restore form data from Redux
    useEffect(() => {
        restoreFormData(coverageReplacementDataRedux, setValue);
    }, [coverageReplacementDataRedux, setValue]);

    // Lock the answer to "No" whenever the live quote state forbids replacement,
    // re-running when the user changes the state in the GIWL Quote Modal.
    useEffect(() => {
        if (isGiwlReplacementReadOnly) {
            setValue("coverageReplacement.isReplacing", false);
            setValue("coverageReplacement.replacementCompany", "");
            setValue("coverageReplacement.replacementAccount", "");
        }
    }, [isGiwlReplacementReadOnly, setValue]);

    // ========== Event Handlers ==========
    const handleRadioChange = useCallback(
        async (value: boolean) => {
            await handleReplacementChange(value, setValue, trigger);
        },
        [setValue, trigger]
    );

    // ========== Ref Methods ==========
    const getReplacementValues = useCallback(() => {
        const formData = form.getValues("coverageReplacement");
        return extractReplacementValues(formData);
    }, [form]);

    useImperativeHandle(
        ref,
        () => ({
            validate: () => trigger("coverageReplacement"),
            getValues: getReplacementValues,
            shouldDisableNext: () => isFloridaReplacement,
        }),
        [trigger, getReplacementValues, isFloridaReplacement]
    );

    // ========== Render ==========
    const radioClass = `radio ${errors.coverageReplacement?.isReplacing ? "radio-error" : "radio-primary"}`;

    return (
        <CardTemplate
            title={
                !isGIWL ? (
                    <h2 className="text-xl font-semibold text-base-content text-center p-2">Coverage Replacement</h2>
                ) : (
                    <div className="flex items-center gap-2 p-2">
                        <h2 className="text-xl font-semibold text-base-content text-center">Coverage Replacement</h2>
                        <InfoCoverageReplacementModal />
                    </div>
                )
            }
            className={className}
        >
            <form className="flex flex-col gap-2" data-testid="coverage-replacement-form" onSubmit={(e) => e.preventDefault()}>
                <p className="text-base-content">{coverageQuestion}</p>

                <div className="flex flex-col mt-1">
                    <div className={`${errors.coverageReplacement?.isReplacing ? "text-error" : ""}`}>
                        <label className="flex items-center gap-2 cursor-pointer h-9 py-2 px-1">
                            <input
                                type="radio"
                                name="replacementIndicator"
                                id="replacementIndicator-yes"
                                value="Yes"
                                checked={isReplacementYes}
                                disabled={isGiwlReplacementReadOnly}
                                onChange={() => handleRadioChange(true)}
                                className={radioClass}
                            />
                            <span className="text-base-content">Yes</span>
                        </label>

                        <label className="flex items-center gap-2 cursor-pointer h-9 py-2 px-1">
                            <input
                                type="radio"
                                name="replacementIndicator"
                                id="replacementIndicator-no"
                                value="No"
                                checked={isReplacementNo}
                                disabled={isGiwlReplacementReadOnly}
                                onChange={() => handleRadioChange(false)}
                                className={radioClass}
                            />
                            <span className="text-base-content">No</span>
                        </label>
                    </div>
                    {errors.coverageReplacement?.isReplacing && (
                        <p className="text-error text-xs mt-1">{errors.coverageReplacement.isReplacing.message}</p>
                    )}
                </div>

                {isFloridaReplacement && <FloridaReplacementNotice />}

                {shouldShowReplacementFields && <ReplacementDetails register={register} setValue={setValue} errors={errors} />}
            </form>
        </CardTemplate>
    );
};

CoverageReplacement.displayName = "CoverageReplacement";

export default CoverageReplacement;
